import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { MoreHorizontal } from "lucide-react";
import clsx from "clsx";
import { POP_END, usePopover } from "./popover";

export interface MoreItem {
  label: ReactNode;
  icon?: ReactNode;
  onClick?: () => void;
  /** Opens in a new tab (a PDF), or downloads (`download`, a list's export). */
  href?: string;
  download?: boolean;
  danger?: boolean;
}

/**
 * A page's less-used actions behind one "More" button, so the few it's mostly for stand
 * out. Items given as false are left out; with none left there's no button. `label` and
 * `icon` name the button for a group of one kind (the ways to share a document). `row` is the
 * small one at the end of a table row, for its rarer and destructive actions (void, cancel).
 */
export function MoreMenu({ items, label, icon, row }: { items: (MoreItem | false | null | undefined)[]; label?: ReactNode; icon?: ReactNode; row?: boolean }) {
  const { t } = useTranslation();
  const { open, setOpen, ref } = usePopover();
  const shown = items.filter((i): i is MoreItem => !!i);
  if (shown.length === 0) return null;
  return (
    <div className={clsx("relative", row && "inline-block")} ref={ref} onClick={row ? (e) => e.stopPropagation() : undefined}>
      <button type="button" className={row ? "btn-ghost p-1" : "btn-secondary"} aria-haspopup="menu" aria-expanded={open} aria-label={row ? t("common.more") : undefined}
        onClick={() => setOpen((o) => !o)}>
        {icon ?? <MoreHorizontal className="size-4" />}{!row && <> {label ?? t("common.more")}</>}
      </button>
      {open && (
        <div role="menu" className={clsx(POP_END, "min-w-48 rounded-[var(--radius-ctl)] py-1")}>
          {shown.map((it, i) => {
            const cls = clsx("flex w-full items-center gap-2 px-3 py-1.5 text-start", it.danger ? "text-danger hover:bg-danger-soft" : "hover:bg-paper");
            return it.href ? (
              <a key={i} role="menuitem" href={it.href} {...(it.download ? { download: true } : { target: "_blank", rel: "noreferrer" })} className={cls} onClick={() => setOpen(false)}>{it.icon}{it.label}</a>
            ) : (
              <button key={i} role="menuitem" type="button" className={cls} onClick={() => { setOpen(false); it.onClick?.(); }}>{it.icon}{it.label}</button>
            );
          })}
        </div>
      )}
    </div>
  );
}
